import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { QrCode, Camera, Scissors, Truck, ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";

const READER_ID = "qr-reader";

function beep(ok = true) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = ok ? 880 : 300;
    g.gain.value = 0.05;
    o.start();
    setTimeout(() => { o.stop(); ctx.close(); }, ok ? 90 : 180);
  } catch { /* no-op */ }
}

const FINISH_REASONS = ["Order for remaining roll", "Expiring material", "Damaged roll"];

export default function Scan() {
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null);
  const [cutLen, setCutLen] = useState("");
  const [cutCustomer, setCutCustomer] = useState("");
  const [finishReason, setFinishReason] = useState("");
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch {}
      try { await s.clear(); } catch {}
    }
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const lookup = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.get("/rolls/lookup", { params: { code } });
      setResult(data);
      setCutLen(""); setCutCustomer("");
      beep(true);
      toast.success(`Found roll ${data.roll.sku}`);
    } catch (e) {
      beep(false);
      toast.error(formatApiError(e.response?.data?.detail) || "No roll found for that code");
    } finally { setBusy(false); }
  };

  const startScan = async () => {
    setResult(null);
    setScanning(true);
    try {
      const html5 = new Html5Qrcode(READER_ID, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.CODE_128],
        verbose: false,
      });
      scannerRef.current = html5;
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          await stopScanner();
          lookup(decodedText);
        },
        () => {},
      );
    } catch (e) {
      setScanning(false);
      toast.error("Could not start camera. Use manual entry below, or check camera permissions.");
    }
  };

  const doManual = () => {
    const code = manual.trim();
    if (code) lookup(code);
  };

  const recordCut = async () => {
    if (!result) return;
    const len = Number(cutLen);
    if (!len || len <= 0) return;
    const finishing = len >= (result.roll.current_length_m ?? 0) - 0.0001;
    if (finishing && !finishReason) return;
    setBusy(true);
    try {
      await api.post(`/rolls/${result.roll.id}/cut`, {
        length_m: len, customer: cutCustomer,
        finish_reason: finishing ? finishReason : undefined,
      });
      toast.success(`Recorded ${len}m cut`);
      setCutLen(""); setCutCustomer(""); setFinishReason("");
      await lookup(result.roll.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const markReceived = async () => {
    if (!result) return;
    setBusy(true);
    try {
      await api.post(`/rolls/${result.roll.id}/receive`, {});
      toast.success("Roll marked received");
      await lookup(result.roll.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const roll = result?.roll;
  const product = result?.product;
  const isIncoming = roll?.status === "incoming";
  const isFinishing = roll && cutLen !== "" && Number(cutLen) > 0 && Number(cutLen) >= (roll.current_length_m ?? 0) - 0.0001;

  return (
    <div className="p-4 sm:p-8 max-w-[720px]" data-testid="scan-page">
      <header className="mb-6 sm:mb-8">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Inventory · Mobile</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <QrCode size={30} /> Scan
        </h1>
        <p className="text-sm text-slate-600 mt-2">Scan a roll label (QR or barcode) to record a cut or receive incoming stock.</p>
      </header>

      <div className="border border-slate-200 bg-white rounded-md p-4 sm:p-5 mb-6">
        {!scanning && (
          <div className="w-full aspect-square max-w-[280px] max-h-[280px] mx-auto mb-4 border-2 border-dashed border-slate-200 rounded-xl grid place-items-center bg-slate-50" data-testid="scan-placeholder">
            <div className="text-center px-4">
              <QrCode size={48} className="mx-auto text-slate-300" />
              <div className="text-xs text-slate-400 mt-2">Point your camera at a roll label</div>
            </div>
          </div>
        )}
        <div id={READER_ID} className={`w-full mx-auto max-w-[320px] ${scanning ? "mb-4" : "hidden"} rounded-md overflow-hidden`} />
        {!scanning ? (
          <Button onClick={startScan} className="w-full h-11 bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="start-scan-btn"><Camera size={18} /> Start camera</Button>
        ) : (
          <Button onClick={stopScanner} variant="outline" className="w-full h-11 gap-2 rounded-md" data-testid="stop-scan-btn">Stop camera</Button>
        )}

        <div className="mt-5 pt-5 border-t border-slate-100">
          <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Or enter SKU manually</Label>
          <div className="flex gap-2 mt-2">
            <div className="relative flex-1 min-w-0">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={manual} onChange={(e) => setManual(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doManual()}
                placeholder="3M-2080-…" className="pl-9 h-11 font-mono" data-testid="manual-code-input" />
            </div>
            <Button onClick={doManual} disabled={!manual.trim() || busy} className="h-11 shrink-0 bg-slate-900 hover:bg-slate-800 rounded-md" data-testid="manual-lookup-btn">Look up</Button>
          </div>
        </div>
      </div>

      {result && roll && (
        <div className="border border-slate-200 bg-white rounded-md p-5" data-testid="scan-result">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              {product?.color_hex && <div className="w-10 h-10 border border-slate-200 rounded-sm" style={{ background: product.color_hex }} />}
              <div>
                <div className="font-heading text-lg font-semibold text-slate-900">{product?.name || "—"}</div>
                <div className="text-xs text-slate-500">{result.manufacturer?.name} · {result.series?.name}</div>
                <div className="font-mono text-xs text-slate-600 mt-0.5">{roll.sku}</div>
              </div>
            </div>
            <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${
              isIncoming ? "bg-amber-50 text-amber-700 border-amber-200" :
              roll.status === "depleted" ? "bg-slate-100 text-slate-500 border-slate-200" :
              "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{roll.status}</span>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5 text-sm">
            <div><div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Length</div><div className="font-mono font-semibold">{(isIncoming ? roll.initial_length_m : roll.current_length_m)?.toFixed?.(2)}m</div></div>
            <div><div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Batch</div><div className="font-mono font-semibold">{roll.batch_number}</div></div>
            <div><div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Location</div><div className="font-mono font-semibold">{roll.location || "—"}</div></div>
          </div>

          {isIncoming ? (
            <Button onClick={markReceived} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2 rounded-md" data-testid="scan-receive-btn">
              <Truck size={16} /> Mark received (into active stock)
            </Button>
          ) : roll.status === "depleted" ? (
            <div className="text-sm text-slate-500 text-center py-2">This roll is depleted.</div>
          ) : (
            <div className="border border-slate-200 rounded-md p-4 bg-slate-50">
              <div className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600 mb-3 flex items-center gap-1.5"><Scissors size={14} /> Record a cut</div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Length (m)</Label>
                  <Input type="number" step="0.01" value={cutLen} onChange={(e) => setCutLen(e.target.value)} placeholder="1.50" className="font-mono" data-testid="scan-cut-length" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-wide text-slate-500">Customer / job (optional)</Label>
                  <Input value={cutCustomer} onChange={(e) => setCutCustomer(e.target.value)} placeholder="Job #123" data-testid="scan-cut-customer" />
                </div>
              </div>
              {isFinishing && (
                <div className="space-y-2 border-t border-slate-200 pt-3 mb-3" data-testid="scan-finish-reason-group">
                  <Label className="text-[10px] font-mono uppercase tracking-wide text-amber-700">This cut finishes the roll — select a reason</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {FINISH_REASONS.map((reason) => (
                      <button
                        key={reason}
                        type="button"
                        onClick={() => setFinishReason(reason)}
                        data-testid={`scan-finish-reason-${reason.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`text-left text-sm rounded-md border px-3 py-2 transition-colors ${finishReason === reason ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-500 text-slate-900 font-medium" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={recordCut} disabled={busy || !cutLen || Number(cutLen) <= 0 || (isFinishing && !finishReason)} className="w-full bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="scan-cut-btn">
                <Scissors size={16} /> Record cut
              </Button>
            </div>
          )}

          <button onClick={() => { setResult(null); setManual(""); }} className="w-full mt-4 text-xs text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1.5" data-testid="scan-again-btn">
            <ArrowClockwise size={13} /> Scan another
          </button>
        </div>
      )}
    </div>
  );
}
